import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

// Legacy localStorage key. We sweep it on mount to remove any stale token
// from the pre-cookie era. Auth now lives exclusively in the httpOnly
// `access_token` cookie set by the backend.
const LEGACY_TOKEN_KEY = "token";

function purgeLegacyToken() {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch (_) {
    /* localStorage unavailable (private mode, sandbox) — ignore */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not auth
  const [ready, setReady] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    // One-time cleanup of any stale localStorage token left over from older
    // builds; cookie-based auth needs nothing on the client.
    purgeLegacyToken();
    refresh();
  }, []);

  const login = async (phone, password) => {
    const { data } = await api.post("/auth/login", { phone, password });
    // We intentionally ignore `data.token` here — the backend has set the
    // httpOnly cookie. Storing the token in localStorage would re-open the
    // XSS vector we just closed.
    await refresh();
    return data;
  };

  const register = async (phone, password, name) => {
    const { data } = await api.post("/auth/register", { phone, password, name });
    await refresh();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore — we still want to clear local state */
    }
    purgeLegacyToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
